import { useMemo, useState } from 'react';
import { useCommissionLedger, useCommissionSummary } from '../../application/hooks/useCommissions';
import { useCurrentUser } from '../../application/hooks/useCurrentUser';
import { useLedgerFilters } from '../../application/hooks/useLedgerFilters';
import { useTranslation } from '../../application/hooks/useTranslation';
import { useWithdrawalHistory } from '../../application/hooks/useWithdrawals';
import { CommissionAdapter } from '../../application/adapters/CommissionAdapter';
import type { CommissionLedgerEntry } from '../../domain/entities/CommissionLedgerEntry';
import { TIER_DESIGNATION_MAP } from '../../domain/enums/TierLevel';
import { formatDhakaDate, formatNumber } from '../../domain/formatters/date';
import { QueryState } from '../components/feedback/QueryState';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { BalanceHero } from '../components/commissions/BalanceHero';
import { CommissionLedgerTable } from '../components/commissions/CommissionLedgerTable';
import { LedgerEntryDetail } from '../components/commissions/LedgerEntryDetail';
import { LedgerFilterBar } from '../components/commissions/LedgerFilterBar';
import { LedgerPager } from '../components/commissions/LedgerPager';
import { MonthlyEarningsChart } from '../components/commissions/MonthlyEarningsChart';
import './pages.css';
import '../components/commissions/commissions.css';

const PAGE_SIZE = 10;
/** The chart reads one unfiltered window; beyond this many entries it only covers the latest rows. */
const CHART_WINDOW = 100;
const IN_PROGRESS = new Set(['PENDING_VERIFICATION', 'PENDING_ADMIN', 'AUTHORIZED']);

/**
 * My earnings: balance hero, 12-month chart, and the filterable ledger.
 * Filters and page live in the URL. Metric limit: ≤ 300 lines.
 */
export default function CommissionsPage() {
  const { t, locale } = useTranslation();
  const filters = useLedgerFilters();
  const { user } = useCurrentUser();
  const [selected, setSelected] = useState<CommissionLedgerEntry | null>(null);

  const summary = useCommissionSummary();
  const withdrawals = useWithdrawalHistory({ page: 1, limit: 10 });
  const ledger = useCommissionLedger({
    page: filters.page,
    limit: PAGE_SIZE,
    month: filters.month || undefined,
    commissionType: filters.type || undefined,
  });
  // Unfiltered window feeding the chart and the "last credit" date (page-1 data would skew both).
  const full = useCommissionLedger({ page: 1, limit: CHART_WINDOW });
  const allEntries = useMemo(() => CommissionAdapter.toDomainList(full.data?.items), [full.data]);
  const pageEntries = useMemo(() => CommissionAdapter.toDomainList(ledger.data?.items), [ledger.data]);

  const requestsInProgress = (withdrawals.data?.items ?? []).filter((r) => IN_PROGRESS.has(r.status)).length;
  const lastCredit = allEntries[0]?.createdAt;
  const capped = (full.data?.total ?? 0) > (full.data?.items.length ?? 0) ? CHART_WINDOW : undefined;

  // Server echoes the effective page — adopt it (clamped pages beyond the end land here).
  const effectivePage = ledger.data?.page ?? filters.page;
  const total = ledger.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="commissions-page" data-testid="commissions-page">
      <div className="page-head">
        <h1 className="page-title">{t('commissions.title')}</h1>
        <div className="page-head-meta">
          {user?.tier && <Badge tone="accent" data-testid="designation-badge">{TIER_DESIGNATION_MAP[user.tier] ?? user.tier}</Badge>}
          {lastCredit && (
            <span className="muted" data-testid="last-credit">
              {t('commissions.lastCredit', { date: formatDhakaDate(lastCredit, locale) })}
            </span>
          )}
        </div>
      </div>

      <QueryState loading={summary.isPending} error={summary.error} onRetry={() => void summary.refetch()} />
      {summary.data && <BalanceHero summary={summary.data} requestsInProgress={requestsInProgress} />}

      <Card title={t('commissions.monthly')}>
        <QueryState loading={full.isPending} error={full.error} onRetry={() => void full.refetch()} skeletonCount={4} />
        {full.data && (
          <MonthlyEarningsChart entries={allEntries} onSelectMonth={filters.setMonth} cappedAt={capped} />
        )}
      </Card>

      <h2 className="page-title section-title">{t('commissions.ledger')}</h2>
      <LedgerFilterBar filters={filters} />

      <QueryState loading={ledger.isPending} error={ledger.error} onRetry={() => void ledger.refetch()} skeletonCount={5} />
      {ledger.data && total === 0 && (
        <Card>
          <div className="empty-box" data-testid="ledger-empty">
            <p>{t(filters.active ? 'commissions.noEntries' : 'commissions.emptyNone')}</p>
            {filters.active && (
              <Button variant="secondary" onClick={filters.clear} data-testid="ledger-empty-clear">
                {t('commissions.clearFilters')}
              </Button>
            )}
          </div>
        </Card>
      )}
      {ledger.data && total > 0 && (
        <Card className={ledger.isPlaceholderData ? 'ledger-busy' : undefined} aria-busy={ledger.isPlaceholderData}>
          {filters.active && (
            <p className="muted ledger-count" data-testid="ledger-count">
              {t('commissions.countLine', {
                shown: formatNumber(pageEntries.length, locale),
                total: formatNumber(total, locale),
              })}
            </p>
          )}
          <CommissionLedgerTable entries={pageEntries} onSelect={setSelected} />
          <LedgerPager page={effectivePage} totalPages={totalPages} onPage={filters.setPage} />
        </Card>
      )}
      <LedgerEntryDetail entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
